<template>
    <TailwindLayout title="Chargeback Disputes">
        <div class="space-y-6">
            <BridgeBackLink />
            <header>
                <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">Chargeback Dispute Evidence</h1>
                <p class="mt-1 text-sm text-gray-600">
                    Recent screening-fee charges. Click a row to open the consolidated
                    evidence view for that charge.
                </p>
            </header>

            <div v-if="$page.props.flash && $page.props.flash.success" class="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
                {{ $page.props.flash.success }}
            </div>
            <div v-if="$page.props.flash && $page.props.flash.error" class="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                {{ $page.props.flash.error }}
            </div>

            <!-- Stripe disputes -->
            <section class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <div>
                        <h2 class="text-lg font-semibold text-gray-900">Stripe disputes</h2>
                        <p class="text-xs text-gray-500">Prepare and review evidence, then submit it to the bank from the dispute page.</p>
                    </div>
                    <button type="button" class="primaryButton" :disabled="syncing" @click="syncDisputes">
                        {{ syncing ? 'Syncing…' : 'Sync from Stripe' }}
                    </button>
                </div>
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Dispute</th>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Respond by</th>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Evidence</th>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-if="stripe_disputes.length === 0">
                                <td colspan="5" class="px-4 py-6 text-sm text-gray-500 text-center">No Stripe disputes saved yet. Sync from Stripe to pull open ones.</td>
                            </tr>
                            <tr
                                v-for="dispute in stripe_disputes"
                                :key="dispute.stripe_dispute_id"
                                class="hover:bg-gray-50 cursor-pointer"
                                @click="openReview(dispute.stripe_dispute_id)"
                            >
                                <td class="px-4 py-3 text-sm font-mono text-gray-700">{{ dispute.stripe_dispute_id }}</td>
                                <td class="px-4 py-3 text-sm">{{ dispute.status || '—' }}</td>
                                <td class="px-4 py-3 text-sm whitespace-nowrap">{{ dispute.respond_by || '—' }}</td>
                                <td class="px-4 py-3 text-sm">{{ evidenceLabel(dispute) }}</td>
                                <td class="px-4 py-3 text-sm">{{ dispute.amount ? '$' + dispute.amount : '—' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <!-- Search -->
            <div class="max-w-xl">
                <label for="dispute-search" class="sr-only">Search disputes</label>
                <input
                    id="dispute-search"
                    v-model="form.search"
                    type="search"
                    placeholder="Search by Stripe charge ID, application ID, or applicant name/email"
                    class="block w-full px-4 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400"
                />
                <p v-if="filters.search" class="mt-2 text-xs text-gray-500">
                    Filtering by <span class="font-mono">"{{ filters.search }}"</span> · {{ invoices.total }} match{{ invoices.total === 1 ? '' : 'es' }}.
                    <button type="button" @click="form.search = ''" class="ml-2 text-rent-risk-blue-400 hover:underline">Clear</button>
                </p>
            </div>

            <!-- Results -->
            <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Applicant Email</th>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Application</th>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Amount</th>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Created</th>
                                <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Charge ID</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-if="invoices.data.length === 0">
                                <td colspan="5" class="px-4 py-6 text-sm text-gray-500 text-center">
                                    <span v-if="filters.search">No matches for <span class="font-mono">"{{ filters.search }}"</span>.</span>
                                    <span v-else>No invoices with Stripe charge IDs found.</span>
                                </td>
                            </tr>
                            <tr
                                v-for="invoice in invoices.data"
                                :key="invoice.id"
                                class="hover:bg-gray-50 cursor-pointer"
                                @click="openDispute(invoice.stripe_charge_id)"
                            >
                                <td class="px-4 py-3 text-sm">{{ invoice.applicant_email || '—' }}</td>
                                <td class="px-4 py-3 text-sm">#{{ invoice.application_id }}</td>
                                <td class="px-4 py-3 text-sm">${{ invoice.charged_amount }}</td>
                                <td class="px-4 py-3 text-sm whitespace-nowrap">{{ invoice.created_at }}</td>
                                <td class="px-4 py-3 text-sm font-mono text-gray-500">{{ invoice.stripe_charge_id }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="invoices.last_page > 1" class="px-4 py-3 border-t border-gray-200">
                    <Pagination :links="invoices.links" :results="invoices" :type="'disputes'" />
                </div>
            </div>
        </div>
    </TailwindLayout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import Pagination from '@/Shared/Pagination.vue';
import throttle from 'lodash/throttle';
import pickBy from 'lodash/pickBy';
import { router } from '@inertiajs/vue3';
import BridgeBackLink from '@/Shared/BridgeBackLink.vue';

export default {
    components: { TailwindLayout, Pagination, BridgeBackLink },
    props: {
        invoices: { type: Object, required: true },
        filters: { type: Object, default: () => ({ search: '' }) },
        stripe_disputes: { type: Array, default: () => [] },
    },
    data() {
        return {
            form: {
                search: this.filters.search ?? '',
            },
            syncing: false,
        };
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function () {
                router.get(
                    route('bridge.disputes.index'),
                    pickBy(this.form),
                    { preserveState: true, preserveScroll: true, replace: true }
                );
            }, 300),
        },
    },
    methods: {
        openDispute(chargeId) {
            router.visit(route('bridge.disputes.show', chargeId));
        },
        openReview(disputeId) {
            router.visit(route('bridge.disputes.review', disputeId));
        },
        syncDisputes() {
            this.syncing = true;
            router.post(route('bridge.disputes.sync'), {}, {
                preserveScroll: true,
                onFinish: () => { this.syncing = false; },
            });
        },
        evidenceLabel(dispute) {
            if (dispute.submitted) return 'Submitted';
            return {
                staged: 'Prepared, awaiting review',
                failed: 'Prepare failed',
                no_evidence_source: 'No evidence; answer in Stripe',
            }[dispute.evidence_stage_status] || 'Not prepared';
        },
    },
};
</script>
